import { NullableDateInput, stringifyDate } from '@superdispatch/dates';
import { startCase } from 'es-toolkit/compat';
import { isEmpty } from 'shared/helpers/CommonHelpers';
import { z } from 'zod';
import { isLoadStageComplete, LoadStage, LOAD_STAGES } from './LoadDTO';

//
// Filter

export const LOAD_FILTER_FIELDS = [
  'drivers',
  'dispatchers',
  'terminals',
] as const;

export type LoadFilterField = (typeof LOAD_FILTER_FIELDS)[number];

//
// Order By

export const LOAD_ORDER_BY_FIELDS = [
  'broker_name',
  'pickup_state',
  'delivery_state',
  'pickup_city',
  'delivery_city',
  'pickup_date',
  'pickedup_date',
  'delivery_date',
  'delivered_date',
  'superpay_status',
  'is_dispatched_to_carrier',
  'none',
] as const;

export type LoadOrderByField = (typeof LOAD_ORDER_BY_FIELDS)[number];

export function isLoadOrderByField(value: unknown): value is LoadOrderByField {
  return (
    typeof value == 'string' &&
    LOAD_ORDER_BY_FIELDS.includes(value as LoadOrderByField)
  );
}

export function formatLoadOrderByField(field: LoadOrderByField): string {
  switch (field) {
    case 'is_dispatched_to_carrier':
      return 'Dispatched to Carrier';
    case 'superpay_status':
      return 'SuperPay';
    case 'broker_name':
      return 'Customer';
    default:
      return startCase(field);
  }
}

//
// Search

export const LOAD_SEARCH_FIELDS = [
  'vin',
  'number',
  'vehicle',
  'order_id',
  'customer',
  'invoice_id',
  'pickup_name',
  'delivery_name',
  'internal_load_id',
  'all',
] as const;

export type LoadSearchField = (typeof LOAD_SEARCH_FIELDS)[number];

export function isLoadSearchField(value: unknown): value is LoadSearchField {
  return (
    typeof value == 'string' &&
    LOAD_SEARCH_FIELDS.includes(value as LoadSearchField)
  );
}

export function findLoadsPageParamsSearchEntry(
  params: LoadsPageParams,
):
  | [undefined, undefined]
  | [LoadSearchField, LoadsPageParams[LoadSearchField]] {
  if (params.stage === 'active') {
    for (let key of LOAD_SEARCH_FIELDS) {
      if (!isEmpty(params[key])) {
        return [key, params[key]];
      }
    }
  }

  return [undefined, undefined];
}

//
// Page Size

export const LOADS_PAGE_SIZES = [10, 25, 50, 100] as const;

export type LoadsPageSize = (typeof LOADS_PAGE_SIZES)[number];

export const DEFAULT_LOADS_PAGE_SIZE: LoadsPageSize = 10;

export function isLoadsPageSize(value: unknown): value is LoadsPageSize {
  return LOADS_PAGE_SIZES.includes(value as LoadsPageSize);
}

// Stages whose endpoint reads `page_size` (LoadsListPagination on the backend).
// The others always return 10 loads.
const PAGE_SIZE_STAGES = new Set<LoadStage>([
  'new',
  'in_terminal',
  'assigned',
  'picked_up',
  'delivered',
  'billed',
  'paid',
  'archived',
  'deleted',
]);

export function canChangeLoadsPageSize(stage: LoadStage): boolean {
  return PAGE_SIZE_STAGES.has(stage);
}

export function getLoadsPageSize(params: LoadsPageParams): number {
  return canChangeLoadsPageSize(params.stage) && params.page_size != null
    ? params.page_size
    : DEFAULT_LOADS_PAGE_SIZE;
}

//
// Params

export function formatLoadsPageField(field: keyof LoadsPageParams) {
  switch (field) {
    case 'number':
      return 'Load ID';
    case 'order_id':
      return 'Order ID';
    case 'invoice_id':
      return 'Invoice ID';
    case 'internal_load_id':
      return 'Internal Load ID';
    case 'vin':
      return 'VIN';
    case 'is_dispatched_to_carrier':
      return 'Dispatched to Carrier';
    case 'pickup_date_after':
    case 'pickup_date_before':
      return 'Pickup Date';
    case 'delivery_date_after':
    case 'delivery_date_before':
      return 'Delivery Date';
    case 'customer':
      return 'Shipper/Customer';
    default:
      return startCase(field);
  }
}

function resolveOrderByField(
  value: null | LoadOrderByField,
  stage: LoadStage,
): null | LoadOrderByField {
  if (value == null) {
    return value;
  }

  const isPickedUp = isLoadStageComplete(stage, 'picked_up');

  if (value === 'pickup_date' && isPickedUp) {
    return 'pickedup_date';
  }

  if (value === 'pickedup_date' && !isPickedUp) {
    return 'pickup_date';
  }

  const isDelivered = isLoadStageComplete(stage, 'delivered');

  if (value === 'delivery_date' && isDelivered) {
    return 'delivered_date';
  }

  if (value === 'delivered_date' && !isDelivered) {
    return 'delivery_date';
  }

  return value;
}

export type LoadsPageParams = z.infer<typeof loadsPageParamsSchema>;

function toStringParam(value: unknown): unknown {
  if (value == null) {
    return null;
  }

  const next = String(value).trim();

  return next.length > 0 ? next : null;
}

const zodStringParam = z.preprocess(
  toStringParam,
  z.string().nullable().default(null).catch(null),
);

function toBooleanParam(value: unknown): unknown {
  if (typeof value === 'boolean') {
    return value;
  }

  if (/^(true|1)$/i.test(String(value))) {
    return true;
  }

  return /^(false|0)$/i.test(String(value)) ? false : null;
}

const zodBooleanParam = z.preprocess(
  toBooleanParam,
  z.boolean().nullable().default(null).catch(null),
);

const zodDateStringParam = z.preprocess(
  (value) => stringifyDate(value as NullableDateInput, { format: 'DateISO' }),
  z.string().nullable().default(null).catch(null),
);

const zodPageParam = z.preprocess((value) => {
  const next = Number(value ?? '');

  return Number.isFinite(next) ? Math.max(1, Math.round(next)) : 1;
}, z.number().default(1).catch(1));

// `null` means the default size, so the URL and the request stay as they were.
// An unsupported size (e.g. `page_size=1000`) falls back to the default.
const zodPageSizeParam = z.preprocess((value) => {
  if (value == null || value === '') {
    return null;
  }

  const next = Number(value);

  return isLoadsPageSize(next) ? next : DEFAULT_LOADS_PAGE_SIZE;
}, z.number().nullable().default(null).catch(DEFAULT_LOADS_PAGE_SIZE));

export const loadsPageParamsSchema = z
  .object({
    /** Internal variable to determine the params shape. */
    stage: z.enum(LOAD_STAGES).catch('new').default('new'),

    //
    // Filter Params

    page: zodPageParam,
    page_size: zodPageSizeParam,

    drivers: zodStringParam,
    dispatchers: zodStringParam,
    terminals: zodStringParam,

    //
    // Search Params

    vin: zodStringParam,
    number: zodStringParam,
    vehicle: zodStringParam,
    order_id: zodStringParam,
    customer: zodStringParam,
    is_empty_customer: zodBooleanParam,
    invoice_id: zodStringParam,
    pickup_name: zodStringParam,
    delivery_name: zodStringParam,
    internal_load_id: zodStringParam,
    all: zodStringParam,
    accounting_method: z
      .enum(['cash', 'accrual'])
      .nullable()
      .catch(null)
      .default(null),

    //
    // Order By Params

    order_by: z.enum(LOAD_ORDER_BY_FIELDS).nullable().catch(null).default(null),

    broker_name: zodStringParam,
    is_empty_broker_name: zodBooleanParam,

    pickup_state: zodStringParam,
    is_empty_pickup_state: zodBooleanParam,

    delivery_state: zodStringParam,
    is_empty_delivery_state: zodBooleanParam,

    pickup_city: zodStringParam,
    is_empty_pickup_city: zodBooleanParam,

    delivery_city: zodStringParam,
    is_empty_delivery_city: zodBooleanParam,

    is_empty_pickup_date: zodBooleanParam,
    pickup_date_after: zodDateStringParam,
    pickup_date_before: zodDateStringParam,

    is_empty_pickedup_date: zodBooleanParam,
    pickedup_date_after: zodDateStringParam,
    pickedup_date_before: zodDateStringParam,

    is_empty_delivery_date: zodBooleanParam,
    delivery_date_after: zodDateStringParam,
    delivery_date_before: zodDateStringParam,

    is_empty_delivered_date: zodBooleanParam,
    delivered_date_after: zodDateStringParam,
    delivered_date_before: zodDateStringParam,

    is_dispatched_to_carrier: zodBooleanParam,

    invoice_date_after: zodDateStringParam,
    invoice_date_before: zodDateStringParam,

    receipt_date_after: zodDateStringParam,
    receipt_date_before: zodDateStringParam,

    superpay_status: zodStringParam,

    //
    // Send Invoice Params

    send_invoice: zodStringParam,
    guids: zodStringParam,
    invoice_date: zodDateStringParam,
    factor_this_invoice: zodBooleanParam,
    bol_template: zodStringParam,
    is_carrier_requested_superpay: zodBooleanParam,
  })
  // The two `when('stage', …)` branches of the Yup schema. They have to run at
  // the object level because they read a sibling field.
  .transform((params) => ({
    ...params,
    terminals: params.stage === 'in_terminal' ? params.terminals : null,
    order_by: resolveOrderByField(params.order_by, params.stage),
  }));
